import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { LinkedinContactStoryblok } from '../../types/storyblok';
import { LinkedIn } from '../ui/icons/social/LinkedIn';

interface LinkedinContactProps {
  blok: LinkedinContactStoryblok;
}

const resolveHref = (link: LinkedinContactStoryblok['linkedin_url']): string | undefined => {
  if (!link) return undefined;
  return link.url || link.cached_url || undefined;
};

const stripScheme = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

export const LinkedinContact: React.FC<LinkedinContactProps> = ({ blok }) => {
  const href = resolveHref(blok.linkedin_url);
  const label = blok.button_label?.trim() || 'Message us on LinkedIn';
  const showUrlDisplay = blok.show_url_display ?? true;

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      className="px-4 py-16 desktop:px-20 desktop:pt-[132px] desktop:pb-[180px]"
    >
      <div className="mx-auto flex w-full max-w-[1062px] flex-col items-center gap-10 desktop:gap-[54px]">
        <div className="flex w-full max-w-[539px] flex-col items-center gap-8 desktop:gap-10">
          {blok.description && (
            <p className="text-body1 text-center text-gray-700">{blok.description}</p>
          )}

          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-deep px-8 py-5 text-button-large font-semibold text-neutral-white shadow-[0_4px_32px_0_rgba(28,43,58,0.16)] hover:bg-brand-blue desktop:w-auto"
            >
              <LinkedIn className="size-6 shrink-0" />
              <span>{label}</span>
            </a>
          )}
        </div>

        {showUrlDisplay && href && (
          <>
            <hr className="w-full border-t border-gray-200" />
            <div className="flex items-center justify-center gap-2">
              <LinkedIn className="size-6 shrink-0 text-gray-700" />
              <span className="text-body1 text-gray-700">{stripScheme(href)}</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
